/**
 * CustomerQualityView — full-page drill-down on customer LTV cohorts.
 *
 * Sections:
 *  1. Hero KPI strip (mean LTV, premium spend share, repeat rate, est value)
 *  2. LTV Decile Distribution chart (which quality tiers your spend lands in)
 *  3. Persona LTV scoreboard (which personas are premium vs bargain)
 *  4. Top 10 quality ads + Bottom 10 waste ads side-by-side
 *  5. AI Action Recommendations sidebar
 */
import { useEffect, useState } from 'react';
import {
  Award,
  Crown,
  Layers,
  Recycle,
  Star,
  Target,
  Trophy,
  Users,
} from 'lucide-react';
import { clsx } from 'clsx';
import {
  creativeWorkbenchApi,
  type WorkbenchCustomerQualityResponse,
  type WorkbenchQualityAd,
  type WorkbenchQualityPersona,
  type WorkbenchQualityTier,
} from '@/services/api';
import { useCreativeWorkbench } from '../CreativeWorkbenchContext';
import { EmbeddedChatPanel, type ChatSeedInput } from './EmbeddedChatPanel';
import {
  ActionRecommendations,
  COMPACT,
  CURRENCY,
  DrillDownHeader,
  KpiTile,
  NUMBER,
} from './_drilldownShared';

const TIER_BADGE: Record<WorkbenchQualityTier, string> = {
  premium: 'bg-teal-100 text-teal-700 ring-teal-200',
  mid: 'bg-slate-100 text-slate-600 ring-slate-200',
  bargain: 'bg-amber-50 text-amber-700 ring-amber-200',
};

const TIER_DOT: Record<WorkbenchQualityTier, string> = {
  premium: 'bg-teal-500',
  mid: 'bg-slate-400',
  bargain: 'bg-amber-400',
};

function HeroKpis({ data }: { data: WorkbenchCustomerQualityResponse }) {
  const totalSpend = data.premium_ad_spend + data.bargain_ad_spend;
  const premiumPct = totalSpend > 0 ? Math.round((data.premium_ad_spend / totalSpend) * 100) : 0;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
      <KpiTile
        label="Mean Customer LTV"
        value={CURRENCY.format(data.deciles.mean_ltv)}
        sub={`${NUMBER.format(data.deciles.n_customers)} customers · ${CURRENCY.format(
          data.deciles.p10,
        )} – ${CURRENCY.format(data.deciles.p90)}`}
        icon={<Crown size={14} className="text-teal-500" />}
        tone="success"
      />
      <KpiTile
        label="Premium Spend Share"
        value={`${premiumPct}%`}
        sub={`${COMPACT.format(data.premium_ad_spend)} on premium-attracting ads`}
        icon={<Award size={14} className="text-teal-500" />}
        tone={premiumPct >= 50 ? 'success' : premiumPct >= 25 ? 'default' : 'warning'}
      />
      <KpiTile
        label="Repeat Buyer Rate"
        value={`${data.overall_repeat_rate_pct}%`}
        sub="customers with 2+ orders"
        icon={<Recycle size={14} className="text-emerald-500" />}
        tone={data.overall_repeat_rate_pct >= 40 ? 'success' : 'default'}
      />
      <KpiTile
        label="Total Est. LTV Value"
        value={COMPACT.format(data.total_estimated_value)}
        sub={`across ${data.top_quality_ads.length + data.low_quality_ads.length} attributed ads`}
        icon={<Star size={14} className="text-indigo-500" />}
      />
      <KpiTile
        label="Bargain Waste"
        value={COMPACT.format(data.bargain_ad_spend)}
        sub={`spend chasing low-LTV cohorts`}
        icon={<Layers size={14} className="text-amber-500" />}
        tone={data.bargain_ad_spend > 1000 ? 'warning' : 'default'}
      />
    </div>
  );
}

function DistributionBars({ data }: { data: WorkbenchCustomerQualityResponse }) {
  const max = Math.max(...data.distribution.map((d) => d.spend), 1);
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-bold text-navy-900 flex items-center gap-1.5">
          <Target size={14} className="text-teal-500" />
          Spend by Customer Quality Score
        </h2>
        <span className="text-[10px] text-slate-400 font-mono">
          0–100 composite · LTV × repeat × ROAS
        </span>
      </div>
      <div className="space-y-2">
        {data.distribution.map((d) => (
          <div key={d.label} className="group flex items-center gap-3">
            <span className="text-[10px] font-mono text-slate-500 w-16 shrink-0">
              {d.label}
            </span>
            <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden">
              <div
                className={clsx(
                  'h-full rounded-full',
                  d.label === '90-100'
                    ? 'bg-teal-500'
                    : d.label === '70-89'
                    ? 'bg-emerald-400'
                    : d.label === '50-69'
                    ? 'bg-slate-400'
                    : d.label === '30-49'
                    ? 'bg-amber-300'
                    : 'bg-red-300',
                )}
                style={{ width: `${(d.spend / max) * 100}%` }}
              />
            </div>
            <div className="text-right w-32 shrink-0">
              <span className="text-[11px] font-bold text-slate-700">
                {CURRENCY.format(d.spend)}
              </span>
              <span className="text-[10px] text-slate-400 ml-2">
                {d.ad_count} ads · {d.spend_share_pct}%
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PersonaLtvBoard({
  personas,
  onAsk,
}: {
  personas: WorkbenchQualityPersona[];
  onAsk: (s: ChatSeedInput) => void;
}) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-bold text-navy-900 flex items-center gap-1.5">
          <Users size={14} className="text-indigo-500" />
          Persona LTV Scoreboard
        </h2>
        <span className="text-[10px] text-slate-400 font-mono">
          ranked by avg LTV · click row to ask
        </span>
      </div>
      <div className="space-y-1">
        {personas.map((p) => (
          <button
            key={p.persona_name}
            onClick={() =>
              onAsk({
                question: `Tell me everything about the '${p.persona_name}' persona — buying behavior, decision factors, and what creative formula attracts them.`,
                chart_axis: { series: 'persona', persona: p.persona_name },
              })
            }
            className="w-full flex items-center gap-3 px-3 py-2 hover:bg-slate-50 rounded-lg transition-colors text-left"
          >
            <span className={clsx('w-1.5 h-8 rounded-full', TIER_DOT[p.tier])} />
            <div className="flex-1 min-w-0">
              <div className="text-[12px] font-semibold text-slate-700 truncate">
                {p.persona_name}
              </div>
              <div className="text-[10px] text-slate-400">
                {NUMBER.format(p.purchasers)} customers · {p.avg_orders.toFixed(1)} orders/cust
              </div>
            </div>
            <span
              className={clsx(
                'text-[9px] font-bold rounded-full px-1.5 py-0.5 ring-1 capitalize',
                TIER_BADGE[p.tier],
              )}
            >
              {p.tier}
            </span>
            <div className="text-right shrink-0 w-24">
              <div className="text-[12px] font-bold text-navy-900">
                {CURRENCY.format(p.avg_ltv)}
              </div>
              <div
                className={clsx(
                  'text-[10px] font-mono',
                  p.ltv_vs_mean_pct >= 0 ? 'text-teal-600' : 'text-amber-600',
                )}
              >
                {p.ltv_vs_mean_pct >= 0 ? '+' : ''}
                {p.ltv_vs_mean_pct.toFixed(0)}% vs mean
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function AdRow({ ad, rank, onAsk }: { ad: WorkbenchQualityAd; rank: number; onAsk: (s: ChatSeedInput) => void }) {
  return (
    <div className="flex items-center gap-3 px-3 py-2.5 bg-white border border-slate-100 rounded-lg hover:border-slate-200 transition-colors">
      <span className="text-[10px] font-bold text-slate-400 w-5 shrink-0">{rank}</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-[12px] font-semibold text-slate-700 truncate" title={ad.ad_name ?? ''}>
            {ad.ad_name ?? ad.ad_id}
          </span>
          <span
            className={clsx(
              'text-[9px] font-bold rounded-full px-1.5 py-0.5 ring-1 capitalize',
              TIER_BADGE[ad.tier],
            )}
          >
            {ad.tier}
          </span>
        </div>
        <div className="text-[10px] text-slate-400 truncate">
          {ad.actual_persona} · {ad.hook_type} · {ad.tone}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3 text-right shrink-0">
        <div>
          <div className="text-[9px] text-slate-400">Spend</div>
          <div className="text-[11px] font-bold text-slate-700">{COMPACT.format(ad.spend)}</div>
        </div>
        <div>
          <div className="text-[9px] text-slate-400">LTV-ROAS</div>
          <div
            className={clsx(
              'text-[11px] font-bold',
              ad.ltv_roas >= 2 ? 'text-teal-600' : ad.ltv_roas >= 0.5 ? 'text-emerald-600' : 'text-slate-500',
            )}
          >
            {ad.ltv_roas.toFixed(2)}x
          </div>
        </div>
        <div>
          <div className="text-[9px] text-slate-400">Q-Score</div>
          <div
            className={clsx(
              'text-[11px] font-bold',
              ad.quality_score >= 70 ? 'text-teal-600' : ad.quality_score >= 40 ? 'text-emerald-600' : 'text-amber-600',
            )}
          >
            {ad.quality_score.toFixed(0)}
          </div>
        </div>
      </div>
      <button
        onClick={() =>
          onAsk({
            question: `Why does ad '${ad.ad_name ?? ad.ad_id}' attract ${ad.tier}-tier customers (${ad.actual_persona}) and what should we do about it?`,
            chart_axis: { series: 'quality', ad_id: ad.ad_id },
          })
        }
        className="text-[10px] text-teal-600 hover:text-teal-700 font-semibold shrink-0 underline-offset-2 hover:underline"
      >
        Ask
      </button>
    </div>
  );
}

function TopAndBottom({
  data,
  onAsk,
}: {
  data: WorkbenchCustomerQualityResponse;
  onAsk: (s: ChatSeedInput) => void;
}) {
  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
      {/* Top quality ads */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-bold text-navy-900 flex items-center gap-1.5">
            <Trophy size={14} className="text-teal-500" />
            Premium-Magnet Ads
          </h2>
          <span className="text-[10px] text-slate-400 font-mono">top 10 by Q-score</span>
        </div>
        <div className="space-y-1.5">
          {data.top_quality_ads.slice(0, 10).map((ad, i) => (
            <AdRow key={ad.ad_id} ad={ad} rank={i + 1} onAsk={onAsk} />
          ))}
        </div>
      </div>

      {/* Low-quality / waste ads */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-bold text-navy-900 flex items-center gap-1.5">
            <Layers size={14} className="text-amber-500" />
            Bargain-Trap Spend
          </h2>
          <span className="text-[10px] text-slate-400 font-mono">high spend · low Q-score</span>
        </div>
        <div className="space-y-1.5">
          {data.low_quality_ads.length === 0 && (
            <div className="text-xs text-slate-400 text-center py-4">
              No high-spend low-quality ads — well-managed!
            </div>
          )}
          {data.low_quality_ads.map((ad, i) => (
            <AdRow key={ad.ad_id} ad={ad} rank={i + 1} onAsk={onAsk} />
          ))}
        </div>
      </div>
    </div>
  );
}

export function CustomerQualityView() {
  const { clientUid } = useCreativeWorkbench();
  const [data, setData] = useState<WorkbenchCustomerQualityResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [chatSeed, setChatSeed] = useState<ChatSeedInput | null>(null);

  useEffect(() => {
    if (!clientUid) return;
    let cancelled = false;
    setLoading(true);
    creativeWorkbenchApi
      .getCustomerQuality({ clientUid })
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [clientUid]);

  return (
    <div className="space-y-5">
      <DrillDownHeader
        breadcrumb="Performance › Customer Quality"
        title="Customer Quality & LTV Cohorts"
        icon={<Crown size={18} className="text-teal-500" />}
        caption="Which creative attracts your best customers — not just the most clicks."
      />

      {loading && !data && (
        <div className="text-sm text-slate-400 animate-pulse py-12 text-center">
          Loading customer quality data...
        </div>
      )}

      {data && (
        <>
          <HeroKpis data={data} />

          <div className="grid grid-cols-12 gap-5">
            <div className="col-span-12 lg:col-span-8 space-y-4">
              <DistributionBars data={data} />
              <PersonaLtvBoard personas={data.personas} onAsk={setChatSeed} />
              <TopAndBottom data={data} onAsk={setChatSeed} />
            </div>
            <aside className="col-span-12 lg:col-span-4 space-y-4">
              <ActionRecommendations
                actions={data.actions}
                onActivate={setChatSeed}
                activeSeed={chatSeed}
                emptyText="No quality recommendations — coverage looks balanced."
              />
              {chatSeed && (
                <EmbeddedChatPanel seed={chatSeed} onClose={() => setChatSeed(null)} />
              )}
            </aside>
          </div>
        </>
      )}
    </div>
  );
}
